




import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import useApi from 'hooks/useApi';
import { Box, Typography, Divider, Table, TableBody, IconButton,
  Tooltip, TableCell, TableContainer, TableHead, TableRow, Paper, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
interface Props {
  teamUid?: string;
  rolesList?: { id: string; uid: string; name: string }[];
  usersList?: { uid: string; name: string }[];
  onEdit?: (notification: any) => void;
  key?: number;
  disabled?: boolean;
}






const NotificationOverview: React.FC<Props> = ({ teamUid, rolesList = [], usersList = [], onEdit, key, disabled = false }) => {
  const { getTeamNotifications, deleteTeamNotification, showToast } = useApi();
  const [notifications, setNotifications] = useState<any[]>([]);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleEdit = (notification: any) => {
    if (disabled) return;
    if (onEdit) onEdit(notification);
  };

  const handleDelete = (notification: any) => {
    if (disabled) return;
    // setDeleteTarget(notification);
    enqueueSnackbar('Are you sure you want to delete this notification?', {
      variant: 'warning',
      persist: true,
      action: (key) => (
        <>
          <button
            style={{ color: '#d32f2f', background: 'none', border: 'none', marginRight: 8, cursor: 'pointer' }}
            onClick={async () => {
              if (!teamUid || !notification.uid) return;
              try {
                await deleteTeamNotification(teamUid, notification.uid);
                showToast('Notification deleted successfully', 'success');
                // Refresh notifications after delete
                const res = await getTeamNotifications(teamUid);
                if (res && res.data) setNotifications(res.data);
              } catch (err) {
                showToast('Failed to delete notification.', 'error');
              }
              closeSnackbar(key);
              // setDeleteTarget(null);
            }}
          >Yes</button>
          <button
            style={{ color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => {
              closeSnackbar(key);
              // setDeleteTarget(null);
            }}
          >No</button>
        </>
      ),
    });
  };

  useEffect(() => {
    if (teamUid && getTeamNotifications) {
      getTeamNotifications(teamUid).then((res: any) => {
        if (res && res.data) setNotifications(res.data);
      });
    }
  }, [teamUid, getTeamNotifications, key]);

  // Helper to map role/user ids to names
  const getRoleNames = (roleIds: string[]) =>
    roleIds.map(uid => {
      const found = rolesList.find(r => r.uid === uid);
      return found ? <Chip key={uid} label={found.name} size="small" sx={{ mr: 0.5 }} /> : <Chip key={uid} label={uid} size="small" sx={{ mr: 0.5 }} />;
    });
  const getUserNames = (userUids: string[]) =>
    userUids.map(uid => {
      const found = usersList.find(u => u.uid === uid);
      return found ? <Chip key={uid} label={found.name} size="small" sx={{ mr: 0.5 }} /> : <Chip key={uid} label={uid} size="small" sx={{ mr: 0.5 }} />;
    });
  const getChannels = (channels: string[]) =>
    channels.map((ch, idx) => <Chip key={ch + idx} label={ch.replace('IN-APP', 'In-App').replace('EMAIL', 'Email').replace('SMS', 'SMS').replace('PRIORITY', 'Tag as Priority(Notification)').replace('ALERT', 'Tag as Priority(Alert)')} size="small" sx={{ mr: 0.5 }} />);
  const getConditions = (conditions: any[]) => {
    if (!conditions || conditions.length === 0) return;
    return conditions.map((c, idx) => {
      if (typeof c === 'object' && c !== null) {
        // Example: {type: 'eta', field: '6888987c6c7112e872d4ad0d', operator: 'subtract', value: 15, valueType: 'minutes'}
        const { type, field, operator, value, valueType } = c;
        let label = '';
        if (type === 'eta') {
          label = `ETA: ${operator === 'subtract' ? '-' : '+'}${value} ${valueType}`;
        } else if (type === 'field_change') {
          label = `${c.fieldOperator || ''}${c.fieldOperator !== 'on change' && c.fieldValue ? ' ' + c.fieldValue : ''}`.trim();
        } else if (type === 'completion') {
          label = `On Completion${c.fieldName ? ': ' + c.fieldName : ''}`;
        } else {
          label = `${type} ${field ? `Field: ${field}` : ''} ${operator ? `Op: ${operator}` : ''} ${value !== undefined ? `Val: ${value}` : ''} ${valueType ? valueType : ''}`.trim();
        }
        return <Chip key={idx} label={label} size="small" sx={{ mr: 0.5 }} />;
      } else if (typeof c === 'string') {
        return <Chip key={c + idx} label={c.replace('ON_COMPLETE', 'On Completion').replace('ON_ETA', 'On ETA').replace('ON_FIELD_CHANGE', 'On Field Value Change')} size="small" sx={{ mr: 0.5 }} />;
      } else {
        return null;
      }
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Notification Overview</Typography>
      <Divider sx={{ mb: 1 }} />
      <TableContainer component={Paper} sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><b>Name</b></TableCell>
              <TableCell><b>Channels</b></TableCell>
              <TableCell><b>Roles</b></TableCell>
              <TableCell><b>Users</b></TableCell>
              <TableCell><b>Conditions</b></TableCell>
              
            </TableRow>
          </TableHead>
          <TableBody>
            {notifications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center"><em>No notifications found</em></TableCell>
              </TableRow>
            ) : (
              notifications.map((n, idx) => (
                <TableRow key={n.uid || idx}>
                  <TableCell sx={{maxWidth:"300px"}}>{n.name || <em>Unnamed</em>}</TableCell>
                  <TableCell>{Array.isArray(n.channels) && n.channels.length > 0 ? getChannels(n.channels) : <em>None</em>}</TableCell>
                  <TableCell>{Array.isArray(n.roles) && n.roles.length > 0 ? getRoleNames(n.roles) : <em>None</em>}</TableCell>
                  <TableCell>{Array.isArray(n.users) && n.users.length > 0 ? getUserNames(n.users) : <em>None</em>}</TableCell>
                  <TableCell>{Array.isArray(n.conditions) && n.conditions.length > 0 ? getConditions(n.conditions) : <em>None</em>}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton 
                        color="primary" 
                        size="small" 
                        onClick={() => handleEdit(n)}
                        disabled={disabled}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        color="error" 
                        size="small" 
                        onClick={() => handleDelete(n)}
                        disabled={disabled}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default NotificationOverview;
